import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Modal, LocationPicker } from '..';
import {
  FiMapPin,
  FiHash,
  FiFlag,
  FiHome,
  FiUser,
  FiMail,
  FiPhone,
  FiFileText,
  FiLayers,
  FiRepeat,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';
import './style.css';

export default function ServiceStationForm({ companyId, initialData, onSuccess, onCancel, companies = [] }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMapModal, setShowMapModal] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [isAlsoCustomer, setIsAlsoCustomer] = useState(false);

  const [formData, setFormData] = useState({
    company_id: companyId,
    name: '',
    corporate_name: '',
    nickname: '',
    type: 'private',
    cnpj: '',
    status: 'ativo',
    rotativo: 'nao',
    responsible_name: '',
    responsible_phone: '',
    responsible_email: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_district: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        company_id: String(initialData.company_id || companyId || ''),
      }));
    } else if (companyId) {
      setFormData(prev => ({ ...prev, company_id: String(companyId) }));
    }
  }, [initialData, companyId]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  function validateForm() {
    const messages = [];

    if (!formData.company_id && (!companies || companies.length === 0)) {
      messages.push('Nenhuma empresa disponível.');
    } else if (!formData.company_id) {
      messages.push('Selecione a empresa.');
    }

    if (!formData.name || !formData.name.trim()) {
      messages.push('Informe o nome do posto.');
    }

    if (!formData.type || !formData.type.trim()) {
      messages.push('Selecione o tipo do posto.');
    }

    if (!formData.status || !formData.status.trim()) {
      messages.push('Informe o status do posto.');
    }

    if (!formData.address_city || !formData.address_city.trim()) {
      messages.push('Informe a cidade.');
    }

    if (!formData.address_state || !formData.address_state.trim()) {
      messages.push('Selecione o estado (UF).');
    }

    if (formData.responsible_email && formData.responsible_email.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.responsible_email)) {
        messages.push('Informe um e-mail de responsável válido.');
      }
    }

    if (messages.length > 0) {
      messages.forEach(message => toast.error(message));
      return false;
    }

    return true;
  }

  async function handleCepBlur() {
    const cep = formData.address_zip_code.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const address = await Api.getAddressByCep(cep);
        if (address && !address.erro) {
          setFormData(prev => ({
            ...prev,
            address_street: address.logradouro,
            address_district: address.bairro,
            address_city: address.localidade,
            address_state: address.uf,
          }));
        } else {
          toast.error('CEP não encontrado.');
        }
      } catch (err) {
        console.error('Erro ao buscar CEP', err);
        toast.error('Erro ao buscar CEP. Verifique se está correto.');
      }
    }
  }

  async function handleCnpjBlur() {
    const cnpj = (formData.cnpj || '').replace(/\D/g, '');
    if (cnpj.length === 14) {
      setCnpjLoading(true);
      try {
        const data = await Api.getCompanyByCnpj(cnpj);
        if (data) {
          const formattedZip = data.cep ? data.cep.replace(/\D/g, '').replace(/^(\d{5})(\d{3})/, '$1-$2') : '';
          setFormData(prev => ({
            ...prev,
            corporate_name: data.razao_social || prev.corporate_name,
            name: data.nome_fantasia || data.razao_social || prev.name,
            address_zip_code: formattedZip || prev.address_zip_code,
            address_street: data.logradouro || prev.address_street,
            address_number: data.numero || prev.address_number,
            address_complement: data.complemento || prev.address_complement,
            address_district: data.bairro || prev.address_district,
            address_city: data.municipio || prev.address_city,
            address_state: data.uf || prev.address_state,
            responsible_phone: data.telefone || prev.responsible_phone,
            responsible_email: data.email || prev.responsible_email,
          }));
        }
      } catch (err) {
        toast.error('Erro ao buscar CNPJ.');
      } finally {
        setCnpjLoading(false);
      }
    }
  }

  function handleLocationSelect(lat, lng) {
    setFormData(prev => ({
      ...prev,
      latitude: String(lat),
      longitude: String(lng)
    }));
    setShowMapModal(false);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        isAlsoCustomer: !isEdit && isAlsoCustomer,
      };

      let result;

      if (isEdit) {
        result = await Api.updateServiceStation(formData.company_id, initialData.id, payload);
      } else {
        result = await Api.createServiceStation(formData.company_id, payload);
      }

      if (result && !result.error) {
        if (onSuccess) onSuccess(result);
      } else {
        setError(result && result.error ? result.error : 'Erro ao salvar posto de serviço.');
      }
    } catch (err) {
      setError('Erro ao processar requisição.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <h3 className="section-title" style={{marginTop: '0px'}}>Dados do posto</h3>
        <div className="form-grid">
          <div className="full-width">
            <Select
              id="company_id"
              label="Empresa *"
              value={String(formData.company_id || '')}
              onChange={handleChange}
              options={[{ value: '', label: 'SELECIONE UMA EMPRESA...' }, ...companies.map(c => ({
                value: String(c.id),
                label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`
              }))]}
              disabled={isEdit} // Disable company change on edit if preferred, or allow if backend supports it
              icon={FiHome}
              searchable
            />
          </div>
          <div className="full-width">
            <Input
              id="name"
              label="Nome do Posto *"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Posto Central"
              required
              icon={FiFileText}
            />
          </div>
          <Input
            id="nickname"
            label="Apelido"
            value={formData.nickname || ''}
            onChange={handleChange}
            placeholder="Ex: Central Matriz"
            icon={FiFileText}
          />
          <Input
            id="corporate_name"
            label="Razão Social"
            value={formData.corporate_name || ''}
            onChange={handleChange}
            placeholder="Ex: Posto Central LTDA"
            icon={FiFileText}
          />
          <Input
            id="cnpj"
            label={cnpjLoading ? 'CNPJ (Buscando...)' : 'CNPJ'}
            value={formData.cnpj || ''}
            onChange={handleChange}
            onBlur={handleCnpjBlur}
            placeholder="00.000.000/0000-00"
            icon={FiHash}
            mask="cnpj"
          />
          <Select
            id="type"
            label="Tipo do Posto"
            value={formData.type}
            searchable
            onChange={handleChange}
            options={[
              { value: 'private', label: 'Privado' },
              { value: 'public', label: 'Público' },
            ]}
            icon={FiLayers}
          />
          <Select
            id="rotativo"
            label="Rotativo"
            value={formData.rotativo}
            onChange={handleChange}
            searchable
            options={[
              { value: 'nao', label: 'Não' },
              { value: 'sim', label: 'Sim' },
            ]}
            icon={FiRepeat}
          />
          <Select
            id="status"
            label="Ativo"
            value={formData.status}
            onChange={handleChange}
            searchable
            options={[
              { value: 'ativo', label: 'Sim' },
              { value: 'inativo', label: 'Não' },
            ]}
            icon={FiFileText}
          />
          {!isEdit && (
            <div 
              className={`feature-toggle-container ${isAlsoCustomer ? 'active' : ''}`} 
              onClick={() => setIsAlsoCustomer(!isAlsoCustomer)}
              style={{ gridColumn: '1 / -1', marginBottom: '0.5rem', marginTop: '0.5rem' }}
            >
              <div className="toggle-track">
                <div className="toggle-thumb" />
              </div>
              <div className="toggle-content">
                <span className="toggle-title">Esse posto de serviço também é um cliente</span>
                <span className="toggle-description">Se marcado, será criado um cliente automaticamente também.</span>
              </div>
            </div>
          )}
        </div>

        <h3 className="section-title">Responsável</h3>
        <div className="form-grid">
          <div className="full-width">
            <Input
              id="responsible_name"
              label="Nome do Responsável"
              value={formData.responsible_name || ''}
              onChange={handleChange}
              placeholder="Nome completo do responsável"
              icon={FiUser}
            />
          </div>
          <Input
            id="responsible_phone"
            label="Telefone / Celular"
            value={formData.responsible_phone || ''}
            onChange={handleChange}
            placeholder="(00) 00000-0000"
            icon={FiPhone}
            mask="phone"
          />
          <Input
            id="responsible_email"
            label="E-mail do responsável"
            value={formData.responsible_email || ''}
            onChange={handleChange}
            icon={FiMail}
          />
        </div>

        <h3 className="section-title">Endereço</h3>
        <div className="form-grid">
          <div className="full-width">
            <Input
              id="address_zip_code"
              label="CEP"
              value={formData.address_zip_code || ''}
              onChange={handleChange}
              onBlur={handleCepBlur}
              placeholder="00000-000"
              icon={FiMapPin}
              mask="cep"
            />
          </div>
          <div className="full-width">
            <Input
              id="address_street"
              label="Rua / Avenida"
              value={formData.address_street || ''}
              onChange={handleChange}
              icon={FiMapPin}
            />
          </div>
          <Input
            id="address_number"
            label="Número"
            value={formData.address_number || ''}
            onChange={handleChange}
            icon={FiHome}
          />
          <Input
            id="address_complement"
            label="Complemento"
            value={formData.address_complement || ''}
            onChange={handleChange}
            icon={FiFileText}
          />
          <Input
            id="address_district"
            label="Bairro"
            value={formData.address_district || ''}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Input
            id="address_city"
            label="Cidade"
            value={formData.address_city || ''}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Select
            id="address_state"
            label="Estado (UF)"
            value={formData.address_state || ''}
            onChange={handleChange}
            searchable
            options={[
              { value: 'AC', label: 'Acre' },
              { value: 'AL', label: 'Alagoas' },
              { value: 'AP', label: 'Amapá' },
              { value: 'AM', label: 'Amazonas' },
              { value: 'BA', label: 'Bahia' },
              { value: 'CE', label: 'Ceará' },
              { value: 'DF', label: 'Distrito Federal' },
              { value: 'ES', label: 'Espírito Santo' },
              { value: 'GO', label: 'Goiás' },
              { value: 'MA', label: 'Maranhão' },
              { value: 'MT', label: 'Mato Grosso' },
              { value: 'MS', label: 'Mato Grosso do Sul' },
              { value: 'MG', label: 'Minas Gerais' },
              { value: 'PA', label: 'Pará' },
              { value: 'PB', label: 'Paraíba' },
              { value: 'PR', label: 'Paraná' },
              { value: 'PE', label: 'Pernambuco' },
              { value: 'PI', label: 'Piauí' },
              { value: 'RJ', label: 'Rio de Janeiro' },
              { value: 'RN', label: 'Rio Grande do Norte' },
              { value: 'RS', label: 'Rio Grande do Sul' },
              { value: 'RO', label: 'Rondônia' },
              { value: 'RR', label: 'Roraima' },
              { value: 'SC', label: 'Santa Catarina' },
              { value: 'SP', label: 'São Paulo' },
              { value: 'SE', label: 'Sergipe' },
              { value: 'TO', label: 'Tocantins' },
            ]}
            icon={FiMapPin}
          />
        </div>

        <h3 className="section-title">Geolocalização</h3>
        <div className="form-grid">
          <Input
            id="latitude"
            label="Latitude"
            value={formData.latitude || ''}
            onChange={handleChange}
            placeholder="-23.550520"
            icon={FiMapPin}
          />
          <Input
            id="longitude"
            label="Longitude"
            value={formData.longitude || ''}
            onChange={handleChange}
            placeholder="-46.633308"
            icon={FiMapPin}
          />
          <div className="full-width" style={{ display: 'flex', justifyContent: 'flex-start' }}>
             <Button
                type="button"
                onClick={() => setShowMapModal(true)}
                variant="outline"
                style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}
             >
                <FiMapPin /> Selecionar no Mapa
             </Button>
          </div>
          <Input
            id="fence_radius_km"
            label="Raio da Cerca (KM)"
            value={formData.fence_radius_km || ''}
            onChange={handleChange}
            placeholder="Ex: 0.5"
            type="number"
            step="0.01"
            icon={FiMapPin}
          />
          <Select
            id="allow_clock_outside_fence"
            label="Bater ponto fora da cerca"
            value={formData.allow_clock_outside_fence ? 'true' : 'false'}
            onChange={(e) => {
               setFormData(prev => ({ ...prev, allow_clock_outside_fence: e.target.value === 'true' }));
            }}
            searchable
            options={[
              { value: 'false', label: 'Não' },
              { value: 'true', label: 'Sim' },
            ]}
            icon={FiFlag}
          />
        </div>

        {error && <div className="form-error">{error}</div>}
      </div>

      <div className="form-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : isEdit ? 'Salvar' : 'Salvar'}
        </Button>
      </div>

      <Modal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        title="Selecionar Localização"
        size="lg"
      >
        {showMapModal && (
          <LocationPicker
            initialLatitude={formData.latitude}
            initialLongitude={formData.longitude}
            onConfirm={handleLocationSelect}
            onCancel={() => setShowMapModal(false)}
          />
        )}
      </Modal>
    </form>
  );
}

